import { IColumn, ICommandBarItemProps, IStyle, ITextStyles, ITheme, mergeStyleSets, useTheme } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPCommandBar, IPSeparator, IPSpinner, IPSpinnerSize, IPStack, IPText, IPTooltipHost } from 'baseComponents';
import { IPSelectionMode } from 'baseComponents/Utils/Selection';
import { AuditTrailEntryCard } from 'components/Activity/AuditTrailEntryCard';
import DialogConfirmDelete from 'components/Dialogs/DialogConfirmDelete';
import GenericModal from 'components/Dialogs/GenericModal';
import BaseTableList, { IBaseTableList } from 'components/TableList/BaseTableList';
import FormTextCorrectionModal from 'components/Tasks/Forms/FormTextCorrectionModal';
import FormVersionPreviewModal from 'components/Tasks/Forms/FormVersionPreviewModal';
import { prepareDesignTimeFormElements } from 'components/Tasks/Forms/taskTypeFormEditor.helpers';
import ScrollableStackItem from 'components/Utils/ScrollableStackItem';
import {
  globalStackTokensGapExtraSmall,
  globalStackTokensGapLarge,
  globalStackTokensGapSmall,
  globalTextStylesBold,
} from 'globalStyles';
import {
  FormPurgePreview,
  FormVersionAmendment,
  FormVersionDiff,
  FormVersionEventActor,
  FormVersionEventType,
  FormVersionFieldDiff,
  FormVersionHistory,
} from 'models/tasks/formVersion';
import TaskType from 'models/tasks/taskType';
import TaskTypeFormElement from 'models/tasks/taskTypeFormElement';
import { Fragment, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  apiCompareFormVersions,
  apiGetFormPurgePreview,
  apiGetFormVersion,
  apiGetFormVersionHistory,
  apiGetTaskTypeForm,
  apiPurgeFormVersion,
} from 'services/Api/taskService';
import { apiRequest } from 'services/Auth/authConfig';
import { toLocaleDateTimeNoSeconds } from 'utils/datetime';
import { sortOnNumber } from 'utils/sorting';

interface IFormVersionHistoryModalProps {
  taskType: TaskType;
  isOpen: boolean;
  onDismiss: () => void;
  onPurged: () => void;
  onCorrected: () => void;
}

// A field's pinned KPI definition reports its OWN changed properties namespaced under the pin, because
// six of them - Category, Folder and the four translated texts - are also field properties and a flat
// list would merge the two into one entry naming neither. The bare code stays the pin itself.
const definitionCode = 'KPIDefinition';
const definitionCodePrefix = `${definitionCode}.`;

interface IChangedPropertyGroups {
  fieldCodes: string[];
  definitionCodes: string[];
}

const groupChangedProperties = (codes: string[]): IChangedPropertyGroups => {
  const definitionCodes = codes.filter((code) => code.startsWith(definitionCodePrefix));
  // The pin becomes the heading of its own sub-list, but only once that sub-list has something in it.
  // With nothing under it - a swapped KPI, or a definition whose properties all match - a heading says
  // less than a plain entry does, so there it stays one.
  const fieldCodes = codes.filter(
    (code) => !code.startsWith(definitionCodePrefix) && !(code === definitionCode && definitionCodes.length > 0),
  );

  return { fieldCodes, definitionCodes };
};

// One shape for the three diff states; only the tint separates them. The label keeps messageText -
// Fluent's colour for text ON these message backgrounds - because the state TEXT colours are not all
// inverted for dark mode (warningText stays near-black there and would vanish into the tint).
const statusBadgeBase: IStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  flexShrink: 0,
  fontSize: 11,
  fontWeight: 600,
  borderRadius: 10,
  padding: '2px 8px',
  whiteSpace: 'nowrap',
};

const getStyles = (theme: ITheme) =>
  mergeStyleSets({
    // The fields of a version are a LIST, so they are drawn as one: a header naming the two columns,
    // then rows separated by a hairline, the way the amendment entries below them are.
    fieldHeader: {
      borderBottom: `1px solid ${theme.semanticColors.bodyDivider}`,
      paddingBottom: 4,
    },
    fieldRow: {
      borderBottom: `1px solid ${theme.semanticColors.bodyDivider}`,
      padding: '6px 0',
    },
    statusAdded: [
      statusBadgeBase,
      { color: theme.semanticColors.messageText, backgroundColor: theme.semanticColors.successBackground },
    ],
    statusDeleted: [
      statusBadgeBase,
      { color: theme.semanticColors.messageText, backgroundColor: theme.semanticColors.errorBackground },
    ],
    // Only the changed badge opens something on hover, so only it says so with the cursor.
    statusChanged: [
      statusBadgeBase,
      {
        color: theme.semanticColors.messageText,
        backgroundColor: theme.semanticColors.warningBackground,
        cursor: 'help',
      },
    ],
    // The changed properties ARE a list, so the tooltip draws one. The browser's own ul is built for
    // body copy - a 1em block margin and a 40px indent - which in a callout this small pushes the
    // entries away from the title that introduces them. The small font size sits on the list rather
    // than only on the text inside it, so the bullets match the size of what they mark.
    changedList: {
      margin: '2px 0 0 0',
      paddingLeft: 16,
      fontSize: theme.fonts.small.fontSize,
    },
    timeline: {
      marginTop: 16,
      paddingTop: 12,
    },
  });

const FormVersionHistoryModal = (props: IFormVersionHistoryModalProps) => {
  const { t } = useTranslation(['forms', 'translation']);
  const appContext = useContext(AppContext);
  const theme = useTheme();
  const styles = getStyles(theme);
  const [history, setHistory] = useState<FormVersionHistory[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<FormVersionHistory | undefined>(undefined);
  // The version the selected one replaced: the snapshot's change badges read against it. The first
  // version has none and shows its fields without them.
  const [previousVersion, setPreviousVersion] = useState<FormVersionHistory | undefined>(undefined);
  // The snapshot list: the version's own fields plus the ones it dropped, borrowed from the previous
  // version so the removal can be named.
  const [formElements, setFormElements] = useState<TaskTypeFormElement[]>([]);
  // The version's own fields only, prepared for rendering as a form - what "Show form" opens.
  const [versionElements, setVersionElements] = useState<TaskTypeFormElement[]>([]);
  const [formVersion, setFormVersion] = useState<TaskType | undefined>(undefined);
  const [diff, setDiff] = useState<FormVersionDiff | undefined>(undefined);
  const [purgePreview, setPurgePreview] = useState<FormPurgePreview | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState<boolean>(false);
  const [showPurge, setShowPurge] = useState<boolean>(false);
  const [showCorrection, setShowCorrection] = useState<boolean>(false);
  const [showFormPreview, setShowFormPreview] = useState<boolean>(false);
  const listRef = useRef<IBaseTableList<FormVersionHistory> | null>(null);
  // Selecting another row while a detail load is in flight starts a second load; only the one for
  // the LAST selected version may write its result, or a slow response would describe another row.
  const detailRequestRef = useRef<number | undefined>(undefined);

  const getColumnsList = (): IColumn[] => [
    {
      key: 'version',
      name: t('forms:Versioning.Columns.Version'),
      minWidth: 50,
      maxWidth: 60,
      isResizable: false,
    },
    {
      // The one column with something to read: it takes what the fixed ones leave, which is why it
      // is the only one without a maxWidth.
      key: 'name',
      name: t('forms:Versioning.Columns.Name'),
      minWidth: 160,
      isRowHeader: true,
      isResizable: true,
    },
    {
      // Wide enough for the date AND the time on one line: two versions a reader has to tell apart
      // are often minutes, not days, apart.
      key: 'date',
      name: t('forms:Versioning.Columns.Changed'),
      minWidth: 120,
      maxWidth: 150,
      isResizable: true,
    },
    {
      // Half again as wide as the other fixed columns need: a full name plus its organisation unit
      // is the longest value in the grid, and truncating it hides which of two namesakes changed it.
      key: 'changedBy',
      name: t('forms:Versioning.Columns.ChangedBy'),
      minWidth: 135,
      maxWidth: 180,
      isResizable: true,
    },
    {
      key: 'numberOfFields',
      name: t('forms:Versioning.Columns.Fields'),
      minWidth: 50,
      maxWidth: 60,
      isResizable: false,
    },
    {
      // The tasks pinned to the version right now - the same number the text correction reports as
      // its impact, so the grid and that dialog never disagree about what "in use" means.
      key: 'taskCount',
      name: t('forms:Versioning.Columns.Tasks'),
      minWidth: 50,
      maxWidth: 60,
      isResizable: false,
    },
  ];

  const [columns, setColumns] = useState<IColumn[]>(getColumnsList());

  useEffect(() => {
    if (!props.isOpen || !props.taskType.taskTypeFamilyId) {
      return;
    }

    void loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isOpen, props.taskType.taskTypeFamilyId]);

  // Bring the version whose detail is on the right into view - on open that is the current one, which
  // on a long history is not the row the grid happens to start on. Selecting it too keeps the grid's
  // checked row in step when the selection comes from code rather than a click (open, purge reload).
  // isLoading is a dependency because the full loader unmounts the grid: history and selection are
  // set while it still shows, so the sync has to run again on the render that mounts the grid.
  useEffect(() => {
    if (isLoading || !selectedVersion) return;
    const index = history.findIndex((version) => version.taskTypeId === selectedVersion.taskTypeId);
    if (index < 0) return;

    listRef.current?.scroll(index, true, history);
  }, [history, selectedVersion, isLoading]);

  const loadHistory = async () => {
    if (!props.taskType.taskTypeFamilyId) {
      return;
    }

    try {
      setIsLoading(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const versions = await apiGetFormVersionHistory(props.taskType.taskTypeFamilyId, accessToken);
      setHistory(versions);
      const current = versions.find((version) => version.isCurrent) ?? versions[0];
      setSelectedVersion(current);
      if (current) {
        await loadVersionDetail(current, versions, accessToken);
      }
    } catch (error) {
      appContext.setError(error);
    } finally {
      setIsLoading(false);
    }
  };

  // The version this one replaced - the highest version number below it. Purging leaves gaps, so it
  // is the nearest lower version rather than version - 1.
  const findPreviousVersion = (
    version: FormVersionHistory,
    versions: FormVersionHistory[],
  ): FormVersionHistory | undefined =>
    versions.reduce<FormVersionHistory | undefined>(
      (previous, candidate) =>
        candidate.version < version.version && (!previous || candidate.version > previous.version)
          ? candidate
          : previous,
      undefined,
    );

  const loadVersionDetail = async (
    version: FormVersionHistory,
    versions = history,
    accessToken?: string,
  ): Promise<void> => {
    if (!props.taskType.taskTypeFamilyId) {
      return;
    }

    try {
      detailRequestRef.current = version.taskTypeId;
      setIsLoadingDetail(true);
      setPurgePreview(undefined);
      const token = accessToken ?? (await appContext.getAccessToken(apiRequest.scopes));
      const previous = findPreviousVersion(version, versions);
      const [versionForm, elements, comparison, previousElements, preview] = await Promise.all([
        apiGetFormVersion(props.taskType.taskTypeFamilyId, version.taskTypeId, token),
        apiGetTaskTypeForm(version.taskTypeId, token),
        // A version is read against the one it replaced: the change badges answer "what did THIS
        // version change?". The first version replaced nothing, so there is no comparison to make and
        // its fields show without badges. The previous version is the compare BASE, so Added/Deleted
        // read from its side: added in this version, removed in this version.
        previous
          ? apiCompareFormVersions(props.taskType.taskTypeFamilyId, previous.taskTypeId, version.taskTypeId, token)
          : Promise.resolve(undefined),
        previous ? apiGetTaskTypeForm(previous.taskTypeId, token) : Promise.resolve<TaskTypeFormElement[]>([]),
        apiGetFormPurgePreview(props.taskType.taskTypeFamilyId, version.taskTypeId, token),
      ]);
      const existingFieldKeys = new Set(elements.map((element) => element.fieldKey ?? element.key));
      const deletedFieldKeys = new Set(
        comparison?.fields.filter((field) => field.status === 'Deleted').map((field) => field.fieldKey) ?? [],
      );
      // A field this version dropped is not in its own element list, so the row that reports it has
      // to be borrowed from the previous version - otherwise the removal could not even be named.
      const removed = previousElements.filter(
        (element) =>
          !!element.fieldKey && deletedFieldKeys.has(element.fieldKey) && !existingFieldKeys.has(element.fieldKey),
      );
      // Sorted and seeded with an empty data row per KPI field: a definition read returns no data,
      // and the form renderer behind "Show form" draws nothing for a KPI field without one.
      const own = prepareDesignTimeFormElements(elements);
      removed.sort((a, b) => sortOnNumber(a.sortOrder, b.sortOrder));
      if (detailRequestRef.current !== version.taskTypeId) {
        return;
      }

      setFormVersion(versionForm);
      setPreviousVersion(previous);
      setVersionElements(own);
      // The dropped fields close the list: they no longer hold a position in this version.
      setFormElements([...own, ...removed]);
      setDiff(comparison);
      setPurgePreview(preview);
    } catch (error) {
      appContext.setError(error);
    } finally {
      if (detailRequestRef.current === version.taskTypeId) {
        setIsLoadingDetail(false);
      }
    }
  };

  // The grid's own selection also fires when the list preselects the current version after a load,
  // which already has its detail loaded - so ignore a selection that changes nothing.
  const selectVersion = (version: FormVersionHistory) => {
    if (version.taskTypeId === selectedVersion?.taskTypeId) {
      return;
    }

    setSelectedVersion(version);
    void loadVersionDetail(version);
  };

  const purge = async () => {
    if (!selectedVersion || !props.taskType.taskTypeFamilyId) {
      return;
    }

    try {
      setIsLoading(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      await apiPurgeFormVersion(props.taskType.taskTypeFamilyId, selectedVersion.taskTypeId, accessToken);
      setShowPurge(false);
      props.onPurged();
      await loadHistory();
    } catch (error) {
      appContext.setError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const corrected = async () => {
    props.onCorrected();
    await loadHistory();
  };

  // The vocabulary is the same on both sides of the namespace, so the prefix is dropped here and it is
  // the caller that decides where the entry is grouped. Stripping it centrally also keeps a namespaced
  // code from reaching the changelog below, which renders these as one flat line.
  const getChangedPropertyText = (code: string): string => {
    const bareCode = code.startsWith(definitionCodePrefix) ? code.substring(definitionCodePrefix.length) : code;
    const [property, languageId] = bareCode.split(':');
    const suffix = languageId ? ` (${languageId})` : '';
    switch (property) {
      case 'Added':
        return t('forms:Versioning.Diff.Added');
      case 'Deleted':
        return t('forms:Versioning.Diff.Deleted');
      case 'Name':
        return `${t('forms:Versioning.Diff.Name')}${suffix}`;
      case 'Description':
        return `${t('forms:Versioning.Diff.Description')}${suffix}`;
      case 'TabName':
        return `${t('forms:Versioning.Diff.TabName')}${suffix}`;
      case 'Instruction':
        return `${t('forms:Versioning.Diff.Instruction')}${suffix}`;
      case 'ExpectedResult':
        return `${t('forms:Versioning.Diff.ExpectedResult')}${suffix}`;
      case 'Type':
        return t('forms:Versioning.Diff.Type');
      case 'Required':
        return t('forms:Versioning.Diff.Required');
      case 'Minimum':
        return t('forms:Versioning.Diff.Minimum');
      case 'Maximum':
        return t('forms:Versioning.Diff.Maximum');
      case 'DecimalPlaces':
        return t('forms:Versioning.Diff.DecimalPlaces');
      case 'DefaultValue':
        return t('forms:Versioning.Diff.DefaultValue');
      case 'Question':
        return t('forms:Versioning.Diff.Question');
      case 'Expression':
        return t('forms:Versioning.Diff.Expression');
      case 'Choices':
        return t('forms:Versioning.Diff.Choices');
      case 'ShowLabel':
        return t('forms:Versioning.Diff.ShowLabel');
      case 'AttachmentRequirement':
        return t('forms:Versioning.Diff.AttachmentRequirement');
      case 'CommentRequirement':
        return t('forms:Versioning.Diff.CommentRequirement');
      case 'Category':
        return t('forms:Versioning.Diff.Category');
      case 'Folder':
        return t('forms:Versioning.Diff.Folder');
      case 'FieldType':
        return t('forms:Versioning.Diff.FieldType');
      case 'Cardinality':
        return t('forms:Versioning.Diff.Cardinality');
      case 'KPIDefinition':
        return t('forms:Versioning.Diff.KPIDefinition');
      case 'Position':
        return t('forms:Versioning.Diff.Position');
      default:
        return code;
    }
  };

  const getDiffForField = (field: TaskTypeFormElement): FormVersionFieldDiff | undefined =>
    diff?.fields.find((item) => item.fieldKey === field.fieldKey);

  const getDiffStatusClass = (status: FormVersionFieldDiff['status']): string => {
    switch (status) {
      case 'Added':
        return styles.statusAdded;
      case 'Deleted':
        return styles.statusDeleted;
      case 'Changed':
        return styles.statusChanged;
    }
  };

  const getDiffStatusText = (status: FormVersionFieldDiff['status']): string => {
    switch (status) {
      case 'Added':
        return t('forms:Versioning.Diff.Added');
      case 'Deleted':
        return t('forms:Versioning.Diff.Deleted');
      case 'Changed':
        return t('forms:Versioning.Diff.Changed');
    }
  };

  const getAmendmentTypeText = (eventType: FormVersionEventType): string => {
    switch (eventType) {
      case FormVersionEventType.HeadAmended:
        return t('forms:Versioning.Timeline.HeadAmended');
      case FormVersionEventType.KPIDefinitionPropagated:
        return t('forms:Versioning.Timeline.KPIDefinitionPropagated');
      case FormVersionEventType.TextCorrected:
        return t('forms:Versioning.Timeline.TextCorrected');
      case FormVersionEventType.SessionDiscarded:
        return t('forms:Versioning.Timeline.SessionDiscarded');
      case FormVersionEventType.KPIDeleted:
        return t('forms:Versioning.Timeline.KPIDeleted');
      case FormVersionEventType.TasksRebound:
        return t('forms:Versioning.Timeline.TasksRebound');
      default:
        return t('forms:Versioning.Timeline.Amended');
    }
  };

  const getAmendmentActorText = (amendment: FormVersionAmendment): string => {
    if (amendment.actorKind === FormVersionEventActor.SystemPropagation) {
      return amendment.triggeredBy
        ? t('forms:Versioning.Timeline.SystemTriggeredBy', { name: amendment.triggeredBy })
        : t('forms:Versioning.System');
    }

    if (amendment.changedBy) {
      return amendment.changedBy;
    }

    switch (amendment.actorKind) {
      case FormVersionEventActor.Projector:
        return t('forms:Versioning.Timeline.Projector');
      case FormVersionEventActor.Copy:
        return t('forms:Versioning.Timeline.Copy');
      case FormVersionEventActor.Migration:
        return t('forms:Versioning.Timeline.Migration');
      default:
        return t('forms:Versioning.Timeline.UnknownUser');
    }
  };

  const canPurge = !!purgePreview && purgePreview.taskTypeId === selectedVersion?.taskTypeId && purgePreview.canPurge;
  // Whether the detail on the right belongs to the row checked on the left. While another row's
  // detail still loads, the commands and the snapshot would otherwise act on or describe the previous
  // row next to the newly checked one.
  const isDetailLoaded = !!selectedVersion && formVersion?.taskTypeId === selectedVersion.taskTypeId;
  // Correction is the escape hatch for versions the editor refuses: the CURRENT version is simply
  // edited in the form editor, so it is not offered here.
  const canCorrect = isDetailLoaded && !selectedVersion.isCurrent;
  // Every version has a form to show, the first one included; it only needs its detail to be in.
  const canShowForm = isDetailLoaded && !isLoadingDetail;
  // The snapshot is the version's own field list, so it shows for every version. Only the change
  // badges need a version behind it to read against: the first version has none and shows the fields
  // the form started with, without badges - the way the KPI definition history shows its first revision.
  const showSnapshot = isDetailLoaded;

  // All commands act on the version selected in the grid, so they live above it rather than in the
  // modal footer, and disable instead of disappear when the selected version does not allow them.
  // Show form comes first: it is the one command every version allows.
  const getCommandBarItems = (): ICommandBarItemProps[] => [
    {
      key: 'showForm',
      text: t('forms:Versioning.ShowForm'),
      iconProps: { iconName: 'Preview' },
      disabled: !canShowForm,
      onClick: () => setShowFormPreview(true),
    },
    {
      key: 'correct',
      text: t('forms:Versioning.Correction.Save'),
      iconProps: { iconName: 'Edit' },
      disabled: !canCorrect,
      onClick: () => setShowCorrection(true),
    },
    {
      key: 'purge',
      text: t('forms:Versioning.PurgeUnused'),
      iconProps: { iconName: 'Delete' },
      disabled: !canPurge,
      onClick: () => setShowPurge(true),
    },
  ];

  const snapshotHeaderStyles: ITextStyles = {
    root: { color: theme.semanticColors.bodySubtext, fontWeight: 600 },
  };

  const renderChangedPropertyList = (codes: string[]): JSX.Element => (
    <ul className={styles.changedList}>
      {codes.map((code) => (
        <li key={code}>
          <IPText variant="small">{getChangedPropertyText(code)}</IPText>
        </li>
      ))}
    </ul>
  );

  // WHAT changed inside a field is a list of its own, and it can run to a dozen entries once a KPI
  // definition moved with it - too much to carry on the row, and exactly what the badge is hovered
  // for. The badge is the only thing that opens this, so the list needs no title of its own saying
  // what it is. The definition's own properties do get a heading and a list under it rather than a
  // place in the field's list: they describe the KPI, not the field, and share half their names with it.
  const renderChangedProperties = (groups: IChangedPropertyGroups): JSX.Element => (
    <IPStack tokens={globalStackTokensGapExtraSmall} styles={{ root: { maxWidth: 260 } }}>
      {groups.fieldCodes.length > 0 && renderChangedPropertyList(groups.fieldCodes)}
      {groups.definitionCodes.length > 0 && (
        <Fragment>
          <IPText variant="small" block styles={globalTextStylesBold}>
            {t('forms:Versioning.Diff.KPIDefinition')}
          </IPText>
          {renderChangedPropertyList(groups.definitionCodes)}
        </Fragment>
      )}
    </IPStack>
  );

  const renderDiffStatus = (fieldDiff: FormVersionFieldDiff): JSX.Element => {
    // The badge counts what the tooltip lists, so the pin is not counted twice once it has become the
    // heading over its own entries.
    const groups = groupChangedProperties(fieldDiff.changedProperties);
    const changedCount = groups.fieldCodes.length + groups.definitionCodes.length;
    const hasProperties = fieldDiff.status === 'Changed' && changedCount > 0;
    const badge = (
      <IPText className={getDiffStatusClass(fieldDiff.status)}>
        {hasProperties
          ? t('forms:Versioning.Diff.ChangedCount', { number: changedCount })
          : getDiffStatusText(fieldDiff.status)}
      </IPText>
    );

    if (!hasProperties) {
      return badge;
    }

    return (
      <IPTooltipHost content={renderChangedProperties(groups)} calloutProps={{ beakWidth: 0 }}>
        {badge}
      </IPTooltipHost>
    );
  };

  const renderSnapshotField = (field: TaskTypeFormElement): JSX.Element => {
    const fieldDiff = getDiffForField(field);
    const fieldName = field.name || field.kpi?.name || t('forms:Versioning.UnnamedField');

    return (
      <IPStack
        key={field.fieldKey ?? field.key}
        horizontal
        horizontalAlign="space-between"
        verticalAlign="center"
        className={styles.fieldRow}
        tokens={globalStackTokensGapSmall}
      >
        <IPStack.Item shrink styles={{ root: { minWidth: 0 } }}>
          <IPText>{fieldName}</IPText>
        </IPStack.Item>
        {fieldDiff && <IPStack.Item shrink={false}>{renderDiffStatus(fieldDiff)}</IPStack.Item>}
      </IPStack>
    );
  };

  const renderVersionColumn = (version: FormVersionHistory | undefined, column: IColumn): JSX.Element | null => {
    if (!version || !column) return null;

    switch (column.key) {
      case 'version':
        return <IPText>{version.version}</IPText>;
      case 'name':
        return <IPText>{version.name}</IPText>;
      case 'date':
        return <IPText>{toLocaleDateTimeNoSeconds(version.date)}</IPText>;
      case 'changedBy':
        return <IPText>{version.changedBy ?? t('forms:Versioning.System')}</IPText>;
      case 'numberOfFields':
        return <IPText>{version.numberOfFields}</IPText>;
      case 'taskCount':
        return <IPText>{version.taskCount}</IPText>;
      default:
        return null;
    }
  };

  return (
    <>
      <GenericModal
        title={t('forms:Versioning.HistoryTitle')}
        subTitle={t('forms:Versioning.HistorySubTitle')}
        isOpen={props.isOpen}
        onClose={props.onDismiss}
        width={'min(94vw, 1300px)'}
        maxWidth={'1300px'}
        minWidth={300}
        height={'min(86vh, 760px)'}
        maxHeight={'86vh'}
        titleSeparator
      >
        {isLoading ? (
          <IPStack horizontalAlign="center" styles={{ root: { paddingTop: 20 } }}>
            <IPSpinner size={IPSpinnerSize.large} />
          </IPStack>
        ) : (
          <IPStack
            horizontal={!appContext.isMobileView}
            tokens={globalStackTokensGapLarge}
            styles={{ root: { height: '100%' } }}
          >
            <IPStack.Item
              styles={{
                root: {
                  // The grid needs every one of its six columns at once - below this share the last
                  // ones fall off the right edge and the list grows a horizontal scrollbar.
                  flex: appContext.isMobileView ? undefined : '0 0 60%',
                  minWidth: 0,
                  height: appContext.isMobileView ? 300 : '100%',
                },
              }}
            >
              <IPStack verticalFill styles={{ root: { height: '100%' } }}>
                <IPStack.Item>
                  <IPCommandBar styles={{ root: { padding: 0 } }} items={getCommandBarItems()} />
                </IPStack.Item>
                <IPStack.Item
                  grow
                  styles={{
                    root: {
                      // BaseTableList draws inside an IPScrollablePane, which needs a positioned parent
                      // with a real height to bound its own scrolling.
                      position: 'relative',
                      minHeight: 0,
                    },
                  }}
                >
                  <BaseTableList
                    ref={listRef}
                    items={history}
                    columns={columns}
                    // The command bar above acts on the selected version, so the grid carries a real
                    // single selection instead of a bare row click - the checked row shows what the
                    // commands and the detail pane apply to.
                    selectionMode={IPSelectionMode.single}
                    isLoading={false}
                    compact
                    noTopPadding
                    getKey={(version) => String(version.taskTypeId)}
                    setColumns={setColumns}
                    onRenderCol={renderVersionColumn}
                    // A version history has one order: the chain the server returns, newest version
                    // first. Reordering it by any column breaks the sequence the changes read in.
                    disableSort
                    // An empty selection is transient (item swaps during reload), never a user intent,
                    // so the detail pane keeps showing the last selected version.
                    onSelect={(versions) => {
                      if (versions.length > 0) selectVersion(versions[0]);
                    }}
                  />
                </IPStack.Item>
              </IPStack>
            </IPStack.Item>
            {/* The two halves are a list and the detail of what is selected in it, so they get the
                same divider the forms scene draws between its rail and its pane. Only side by side:
                stacked on mobile a vertical rule has no height to draw. */}
            {!appContext.isMobileView && <IPSeparator vertical />}
            <IPStack.Item
              grow
              styles={{
                root: {
                  minWidth: 0,
                  minHeight: 0,
                  height: appContext.isMobileView ? undefined : '100%',
                  paddingTop: appContext.isMobileView ? 12 : 0,
                },
              }}
            >
              {/* The grid on the left brought its own scroll pane, so the detail scrolls on its own
                  too - otherwise a long snapshot would push the whole modal body instead. */}
              <IPStack verticalFill styles={{ root: { height: '100%', minHeight: 0 } }}>
                <ScrollableStackItem minHeight={100} disableScroll={appContext.isMobileView}>
                  {/* The rows in here are right-aligned against the pane's own scrollbar, so the
                      content keeps a gutter away from it instead of touching it. */}
                  <IPStack styles={{ root: { paddingRight: 10 } }}>
                    {/* While another version's detail loads, the pane would otherwise keep describing
                        the PREVIOUS row next to the newly checked one. */}
                    {isLoadingDetail && (
                      <IPStack horizontalAlign="center" styles={{ root: { paddingTop: 32 } }}>
                        <IPSpinner size={IPSpinnerSize.large} />
                      </IPStack>
                    )}
                    {/* The fields of the selected version, for every version: the modal's subtitle
                        promises them. The change column is only there when a previous version gives the
                        badges something to read against. */}
                    {!isLoadingDetail && showSnapshot && (
                      <Fragment>
                        {/* The two section headings here are body text in bold, like every other heading
                            in the app: mediumPlus made them read as a second modal title. */}
                        <IPText block styles={globalTextStylesBold}>
                          {t('forms:Versioning.Snapshot')}
                        </IPText>
                        <IPText variant="small" block styles={{ root: { paddingTop: 2 } }}>
                          {previousVersion
                            ? t('forms:Versioning.SnapshotDescription', { version: previousVersion.version })
                            : t('forms:Versioning.SnapshotDescriptionFirst')}
                        </IPText>
                        <IPStack styles={{ root: { marginTop: 12 } }}>
                          {formElements.length === 0 ? (
                            <IPText>{t('forms:Versioning.SnapshotEmpty')}</IPText>
                          ) : (
                            <Fragment>
                              {/* Says in two words what the rows below are, so the list is never read as
                                  loose lines of text under a heading. */}
                              <IPStack
                                horizontal
                                horizontalAlign="space-between"
                                verticalAlign="center"
                                className={styles.fieldHeader}
                                tokens={globalStackTokensGapSmall}
                              >
                                <IPText variant="small" styles={snapshotHeaderStyles}>
                                  {t('forms:Versioning.SnapshotField')}
                                </IPText>
                                {previousVersion && (
                                  <IPText variant="small" styles={snapshotHeaderStyles}>
                                    {t('forms:Versioning.SnapshotChange')}
                                  </IPText>
                                )}
                              </IPStack>
                              {formElements.map((field) => renderSnapshotField(field))}
                            </Fragment>
                          )}
                        </IPStack>
                      </Fragment>
                    )}
                    {!isLoadingDetail && selectedVersion && (
                      <IPStack
                        className={showSnapshot ? styles.timeline : undefined}
                        tokens={globalStackTokensGapSmall}
                      >
                        <IPStack.Item>
                          <IPText block styles={globalTextStylesBold}>
                            {t('forms:Versioning.Timeline.Title')}
                          </IPText>
                        </IPStack.Item>
                        {/* An amendment IS a change log entry, so it is drawn by the change log's own
                            card component - the one the audit trail renders with - instead of a lookalike
                            that would drift away from it. */}
                        {selectedVersion.amendments.length === 0 ? (
                          <IPText>{t('forms:Versioning.Timeline.Empty')}</IPText>
                        ) : (
                          selectedVersion.amendments.map((amendment) => (
                            <AuditTrailEntryCard
                              key={amendment.formVersionEventId}
                              userId={amendment.changedById}
                              userName={amendment.changedBy ?? t('forms:Versioning.System')}
                              date={amendment.date}
                              actor={getAmendmentActorText(amendment)}
                              text={getAmendmentTypeText(amendment.eventType)}
                              detail={
                                amendment.changedProperties.length > 0
                                  ? amendment.changedProperties.map(getChangedPropertyText).join(', ')
                                  : undefined
                              }
                            />
                          ))
                        )}
                      </IPStack>
                    )}
                  </IPStack>
                </ScrollableStackItem>
              </IPStack>
            </IPStack.Item>
          </IPStack>
        )}
      </GenericModal>
      {showPurge && selectedVersion && (
        <DialogConfirmDelete
          hidden={!showPurge}
          title={t('forms:Versioning.PurgeTitle')}
          subText={t('forms:Versioning.PurgeText', { version: selectedVersion.version })}
          onNo={() => setShowPurge(false)}
          onYes={() => void purge()}
        />
      )}
      {showCorrection && formVersion && (
        <FormTextCorrectionModal
          taskType={formVersion}
          isOpen={showCorrection}
          onDismiss={() => setShowCorrection(false)}
          onCorrected={corrected}
        />
      )}
      {showFormPreview && formVersion && (
        <FormVersionPreviewModal
          taskType={formVersion}
          formElements={versionElements}
          isOpen={showFormPreview}
          onDismiss={() => setShowFormPreview(false)}
        />
      )}
    </>
  );
};

export default FormVersionHistoryModal;
